import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, oauthGoogleStartUrl } from '../api/client';
import AuthCard from '../components/AuthCard';
import Button from '../components/Button';
import Field from '../components/Field';
import FormBanner from '../components/FormBanner';
import { useAuth } from '../auth/AuthContext';
import { errorMessage } from '../lib/errors';
import { EMAIL_MAX_LENGTH, PASSWORD_MAX_LENGTH, emailError } from '../lib/validation';

// The Google OAuth callback is a full-page redirect, so it can't return an
// error in a fetch body — it bounces back here with `?oauth_error=<code>` and we
// render a friendly banner instead of leaving the user on a raw JSON page.
const OAUTH_ERRORS: Record<string, string> = {
  email_unverified:
    "Google couldn't confirm this email is verified, so we didn't sign you in. Use the email and password for this account, or sign in with a Google account whose email is verified.",
  account_disabled: 'This account has been disabled. Contact support if you believe this is a mistake.',
  google_failed: "We couldn't complete Google sign-in. Please try again.",
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Default landing is "/" → the editor-first resolver (last/recent/auto-create).
  // A saved `from` (deep-link bounce) is honoured so a protected link still returns.
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(
    () => OAUTH_ERRORS[searchParams.get('oauth_error') ?? ''] ?? null,
  );
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: { email?: string; password?: string } = {};
    const eErr = emailError(email);
    if (eErr) errs.email = eErr;
    if (!password) errs.password = 'Enter your password.';
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;

    setFormError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      // The password was right but the inbox is unproven: the server issued a
      // pending token (not a session) and mailed a fresh code, so carry on to the
      // code screen rather than showing an error. Only reachable AFTER the
      // password verifies, so it reveals nothing a caller doesn't already know.
      if (err instanceof ApiError && err.code === 'EMAIL_UNVERIFIED') {
        navigate('/verify-email', { replace: true, state: { from } });
        return;
      }
      // Every other failure (wrong password, unknown email, disabled account)
      // returns the same "invalid credentials" — the backend is enumeration-safe.
      setFormError(errorMessage(err, 'Could not sign you in. Check your details and try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      eyebrow="Welcome back"
      title="Sign in"
      subtitle="Pick up right where you left off."
      footer={
        <>
          New here?{' '}
          <Link to="/register" className="font-medium text-primary hover:opacity-80">
            Create an account
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        {formError ? <FormBanner kind="error">{formError}</FormBanner> : null}
        <Field
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          maxLength={EMAIL_MAX_LENGTH}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <Field
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          maxLength={PASSWORD_MAX_LENGTH}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
        />
        <div className="text-right">
          <Link to="/forgot" className="text-xs text-muted-foreground hover:text-foreground">
            Forgot password?
          </Link>
        </div>
        <Button type="submit" loading={submitting} className="h-11 w-full text-base">
          Sign in
        </Button>
      </form>

      <div className="flex items-center gap-3 brand-eyebrow text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>

      {/* A full-page navigation (not fetch): the browser follows the backend's
          307 to Google. The OAuth flow is entirely backend-driven redirects. */}
      <a
        href={oauthGoogleStartUrl}
        className="flex h-11 w-full items-center justify-center rounded-md border border-border bg-card px-4 text-center text-sm font-medium text-foreground transition-colors hover:bg-accent"
      >
        Continue with Google
      </a>
    </AuthCard>
  );
}
